import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { ShoppingCart, BookOpen } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const CourseDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourse();
  }, [id]);

  const fetchCourse = async () => {
    try {
      const response = await api.get(`/courses/${id}`);
      setCourse(response.data);
    } catch (error) {
      console.error('Failed to fetch course:', error);
      toast.error('Course not found');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!user) {
      toast.error('Please login to add to cart');
      return;
    }
    addToCart(course.id, 'course', 1);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-2xl">Course not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="course-detail-page">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Course Thumbnail */}
          <div>
            {course.thumbnail ? (
              <div className="aspect-video rounded-2xl overflow-hidden">
                <img
                  src={course.thumbnail}
                  alt={course.name}
                  className="w-full h-full object-cover"
                  data-testid="course-thumbnail"
                />
              </div>
            ) : (
              <div className="aspect-video rounded-2xl bg-white/5 flex items-center justify-center">
                <BookOpen className="w-16 h-16 text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Course Info */}
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="course-name">
                {course.name}
              </h1>
            </div>

            <div className="flex items-center gap-4">
              {course.original_price && (
                <span className="text-2xl text-muted-foreground line-through">
                  ₹{course.original_price}
                </span>
              )}
              <span className="text-4xl font-bold text-primary" data-testid="course-price">
                ₹{course.price}
              </span>
            </div>

            <div className="prose prose-invert max-w-none">
              <p className="text-muted-foreground text-lg leading-relaxed" data-testid="course-description">
                {course.description}
              </p>
            </div>

            {/* Modules */}
            {course.modules && course.modules.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-2xl font-semibold">Course Curriculum</h3>
                <div className="space-y-3">
                  {course.modules.map((module, index) => (
                    <div key={index} className="p-4 bg-white/5 rounded-xl border border-white/10">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary font-bold text-sm">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold mb-1">{module.title}</h4>
                          {module.lessons && (
                            <p className="text-sm text-muted-foreground">
                              {module.lessons.length} lessons
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={handleAddToCart}
              className="w-full btn-primary flex items-center justify-center gap-2"
              data-testid="add-to-cart-button"
            >
              <ShoppingCart className="w-5 h-5" />
              Enroll Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;
